import { BarChart3, Building2, ShieldCheck } from 'lucide-react';
import { ReactNode } from 'react';

function BrandLogo({ className = '' }: { className?: string }) {
  return (
    <img
      src='/images/Logo.jpg'
      alt='TUTIMI'
      className={`rounded-xl border border-white/10 object-cover ${className}`}
    />
  );
}

function BrandLockup() {
  return (
    <div className='flex items-center gap-3.5'>
      <BrandLogo className='h-12 w-12 shrink-0' />
      <div className='leading-none'>
        <div className='text-2xl font-extrabold tracking-tight text-white'>
          TTMI <span className='text-[#ff5959]'>ERP</span>
        </div>
        <div className='mt-1 text-[10px] font-bold uppercase tracking-[0.24em] text-blue-200/60'>Enterprise Suite</div>
      </div>
    </div>
  );
}

export function AuthShell({ children }: { children: ReactNode }) {
  return (
    <main className='grid min-h-screen w-full bg-slate-50 font-sans text-slate-900 lg:grid-cols-[minmax(520px,56%)_minmax(420px,44%)]'>
      {/* Left section - Static Branding Info */}
      <section className='relative hidden min-h-screen overflow-hidden bg-[#023e8a] px-16 py-12 text-white lg:flex lg:flex-col lg:justify-between'>
        {/* Subtle background decoration */}
        <div className='absolute right-0 top-0 -mr-24 -mt-24 h-96 w-96 rounded-full bg-white/5 blur-3xl' />
        <div className='absolute bottom-0 left-0 -mb-24 -ml-24 h-96 w-96 rounded-full bg-blue-400/10 blur-3xl' />

        <div className='relative z-10'>
          <BrandLockup />
        </div>

        <div className='relative z-10 my-auto max-w-[580px] py-12'>
          <p className='mb-3 text-[11px] font-bold uppercase tracking-[0.25em] text-blue-300'>
            Nền tảng vận hành tập trung
          </p>
          <h1 className='text-4xl font-extrabold leading-[1.2] tracking-tight text-white sm:text-5xl'>
            Quản trị dữ liệu doanh nghiệp rõ ràng, nhanh và liền mạch.
          </h1>
          <p className='mt-5 max-w-[480px] text-sm leading-relaxed text-blue-100/70'>
            Truy cập hệ thống ERP TTMI để xử lý bán hàng, mua hàng, tài chính, kho và danh mục nghiệp vụ trong một giao
            diện thống nhất.
          </p>
        </div>

        <div className='relative z-10 grid max-w-[680px] grid-cols-3 gap-5'>
          {[
            { icon: ShieldCheck, label: 'Bảo mật phiên đăng nhập', desc: 'Mã hóa tối đa' },
            { icon: BarChart3, label: 'Dữ liệu nghiệp vụ tức thời', desc: 'Báo cáo realtime' },
            { icon: Building2, label: 'Quy trình đa phòng ban', desc: 'Vận hành liền mạch' }
          ].map(item => (
            <div
              key={item.label}
              className='group rounded-2xl border border-white/5 bg-white/[0.02] p-5 backdrop-blur-md transition-all duration-300 hover:border-white/10 hover:bg-white/[0.05]'
            >
              <div className='mb-3 flex h-9 w-9 items-center justify-center rounded-xl bg-white/10 text-white transition-all duration-300 group-hover:bg-white/20'>
                <item.icon className='h-4.5 w-4.5' />
              </div>
              <p className='text-xs font-semibold leading-5 text-white'>{item.label}</p>
              <p className='mt-0.5 text-[10px] text-blue-200/50'>{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Right section - Interactive Form container */}
      <section className='flex min-h-screen w-full items-center justify-center border-l border-slate-100 bg-[#f8fafc] px-6 py-10 sm:px-10 lg:px-16'>
        <div className='w-full max-w-[450px]'>
          {/* Mobile brand header (visible on lg:hidden) */}
          <div className='mb-8 flex justify-center lg:hidden'>
            <div className='flex items-center gap-3'>
              <BrandLogo className='h-11 w-11 shadow-sm' />
              <div className='text-2xl font-black tracking-tight text-slate-900'>
                TTMI <span className='text-[#ef4444]'>ERP</span>
              </div>
            </div>
          </div>

          {/* Form wrapper with fine flat border and subtle modern shadow */}
          <div className='rounded-2xl border border-slate-100 bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.015)] sm:p-10'>
            {children}
          </div>

          <p className='mt-8 text-center text-xs font-medium text-slate-400'>
            Bản quyền © 2025 thuộc về TTMI. Bảo lưu mọi quyền.
          </p>
        </div>
      </section>
    </main>
  );
}
